export interface SnapshotRow {
  operatorId: string;
  operatorName: string;
  storeId: string;
  storeName: string;
  campaignId: string;
  campaignName: string;
  formatId?: string;
  formatName: string;
  expectedQty: number;
  mediaFormatName?: string;
  expected?: number;
  expectedQuantity?: number;
  quantity?: number;
}

export interface OperatorTask {
  cycleId: string | number;
  operatorId: string;
  operatorName: string;
  items: Array<{
    storeId: string;
    storeName: string;
    campaignId: string;
    campaignName: string;
    formatId?: string;
    formatName: string;
    expectedQty: number;
  }>;
}

export function buildOperatorTasksFromSnapshot(
  snapshot: any,
  operatorId: string,
  cycleId: string | number
): OperatorTask {
  console.log('SNAPSHOT_TASK_BUILD', { operatorId, snapshot: !!snapshot });

  if (!snapshot) {
    console.log('SNAPSHOT_TASK_BUILD: No snapshot data');
    return {
      cycleId,
      operatorId: String(operatorId),
      operatorName: String(operatorId),
      items: [],
    };
  }

  // Handle different snapshot formats (both legacy and new hardened format)
  let assignmentRows: any[] = [];
  
  // Try multiple paths for finding assignment data
  if (snapshot.campaignSnapshots && Array.isArray(snapshot.campaignSnapshots)) {
    // Legacy format: extract from campaignSnapshots
    assignmentRows = snapshot.campaignSnapshots.flatMap((campaignSnapshot: any) => 
      (campaignSnapshot?.snapshotData?.packs ?? []).flatMap((pack: any) => 
        (pack?.storeOperatorAssignments ?? []).map((assignment: any) => ({
          operatorId: assignment.operatorId,
          operatorName: assignment.operatorName,
          storeId: assignment.storeId,
          storeName: assignment.storeName,
          campaignId: campaignSnapshot.campaignId,
          campaignName: campaignSnapshot.campaignName,
          taskId: pack.taskId,
          taskName: pack.taskName,
          formatId: assignment.mediaFormatId,
          formatName: assignment.mediaFormatName,
          expectedQty: assignment.quantity,
        }))
      )
    );
  } else {
    // Try direct paths for flat assignment data
    assignmentRows = snapshot?.assignments ?? 
                    snapshot?.assignmentRows ?? 
                    snapshot?.data?.assignments ?? 
                    snapshot?.data?.assignmentRows ?? 
                    [];
  }

  console.log('SNAPSHOT_TASK_BUILD: Found assignment rows', assignmentRows.length);

  const rows: SnapshotRow[] = assignmentRows.filter((r: any) => 
    String(r.operatorId) === String(operatorId)
  );

  console.log('SNAPSHOT_TASK_BUILD: Filtered for operator', operatorId, 'count:', rows.length);

  const items = rows.map(r => ({
    storeId: String(r.storeId),
    storeName: r.storeName ?? String(r.storeId),
    campaignId: String(r.campaignId),
    campaignName: r.campaignName ?? String(r.campaignId),
    formatId: r.formatId ? String(r.formatId) : undefined,
    formatName: r.formatName ?? r.mediaFormatName ?? 'Unknown',
    expectedQty: Number(r.expected ?? r.expectedQty ?? r.expectedQuantity ?? r.quantity ?? 0),
  }));

  // Get operator name from snapshot metadata or first row
  let operatorName = rows[0]?.operatorName;
  if (!operatorName && snapshot?.operatorsById) {
    operatorName = snapshot.operatorsById[operatorId]?.name;
  }
  if (!operatorName) {
    operatorName = String(operatorId);
  }

  console.log('SNAPSHOT_TASK_BUILD: Result', { operatorId, operatorName, itemCount: items.length });

  return {
    cycleId,
    operatorId: String(operatorId),
    operatorName,
    items,
  };
}

export function countTasks(t: OperatorTask): number {
  return t.items.length;
}

/**
 * Tiny smoke test for task building function
 */
export function smokeTestTaskBuilding(): boolean {
  const mockSnapshot = {
    campaignSnapshots: [
      {
        campaignId: 123,
        campaignName: "Test Campaign",
        snapshotData: {
          packs: [
            {
              taskId: 456,
              taskName: "Test Task",
              storeOperatorAssignments: [
                {
                  operatorId: 789,
                  operatorName: "Test Operator",
                  storeId: 101,
                  storeName: "Test Store",
                  mediaFormatId: 202,
                  mediaFormatName: "Test Format",
                  quantity: 5
                }
              ]
            }
          ]
        }
      }
    ]
  };

  const result = buildOperatorTasksFromSnapshot(mockSnapshot, "789", "999");
  
  const isValid = result.items.length === 1 && 
                  result.operatorId === "789" &&
                  result.items[0].storeId === "101" &&
                  result.items[0].expectedQty === 5;

  console.log('SMOKE_TEST_RESULT', { isValid, itemCount: result.items.length });
  return isValid;
}

/**
 * Send operator tasks to mobile using the existing API endpoint
 */
/**
 * Build operator tasks from processed rows array (what the table uses)
 */
export function buildOperatorTasksFromProcessedRows(
  processedRows: any[],
  operatorId: string | number,
  operatorName: string,
  cycleId: string | number
): OperatorTask {
  if (!processedRows || !Array.isArray(processedRows)) {
    console.log('PROCESSED_ROWS_BUILD: No processed rows data');
    return {
      cycleId,
      operatorId: String(operatorId),
      operatorName: String(operatorName),
      items: [],
    };
  }

  console.log('PROCESSED_ROWS_BUILD: Processing', processedRows.length, 'rows for operator', operatorId);

  // Filter rows for this specific operator
  const operatorRows = processedRows.filter((r: any) => 
    String(r.operatorId) === String(operatorId)
  );

  console.log('PROCESSED_ROWS_BUILD: Found', operatorRows.length, 'rows for operator', operatorId);

  const items = operatorRows.map(r => ({
    storeId: String(r.storeId),
    storeName: r.storeName ?? String(r.storeId),
    campaignId: String(r.campaignId),
    campaignName: r.campaignName ?? String(r.campaignId),
    formatId: r.formatId ? String(r.formatId) : undefined,
    formatName: r.formatName ?? r.mediaFormatName ?? 'Unknown',
    expectedQty: Number(r.expectedQty ?? r.expected ?? r.expectedQuantity ?? r.quantity ?? 0),
  }));

  console.log('PROCESSED_ROWS_BUILD: Result', { operatorId, operatorName, itemCount: items.length });

  return {
    cycleId,
    operatorId: String(operatorId),
    operatorName: String(operatorName),
    items,
  };
}

/**
 * Create snapshot adapter for fallback (when table rows are empty)
 */
export function rowsFromLockedSnapshot(snapshot: any): SnapshotRow[] {
  if (!snapshot) return [];
  
  // Handle different snapshot formats
  const assignmentRows = snapshot?.assignments ?? 
                        snapshot?.assignmentRows ?? 
                        snapshot?.data?.assignments ?? 
                        snapshot?.data?.assignmentRows ?? 
                        [];

  return assignmentRows.map((r: any) => ({
    operatorId: String(r.operatorId),
    operatorName: r.operatorName ?? String(r.operatorId),
    storeId: String(r.storeId),
    storeName: r.storeName ?? String(r.storeId),
    campaignId: String(r.campaignId),
    campaignName: r.campaignName ?? String(r.campaignId),
    formatId: r.formatId ? String(r.formatId) : undefined,
    formatName: r.formatName ?? r.mediaFormatName ?? 'Unknown',
    expectedQty: Number(r.expected ?? r.expectedQty ?? r.expectedQuantity ?? r.quantity ?? 0),
  }));
}

type SendOperatorTasksOptions = {
  includeBriefing?: boolean;
};

export async function sendOperatorTasks(
  tasks: OperatorTask,
  options: SendOperatorTasksOptions = {},
): Promise<{ success: boolean; tasksUpdated: number; message: string; sentAt?: string }> {
  const includeBriefing = options.includeBriefing === true;

  console.log('SEND_TASKS_PAYLOAD', { 
    operatorId: tasks.operatorId, 
    items: tasks.items.length,
    includeBriefing,
  });

  // Import apiFetch for proper Supabase JWT authentication
  const { apiFetch } = await import('./api');

  // Use the unified mark-sent endpoint
  const response = await apiFetch('/api/assignments/mark-sent', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      cycleId: Number(tasks.cycleId),
      operatorId: Number(tasks.operatorId),
      method: 'mobile',
      includeBriefing,
    }),
  });

  if (!response.ok) {
    let errorMessage = `Failed to send tasks: ${response.status} ${response.statusText}`;
    try {
      const errorBody = await response.json();
      if (typeof errorBody?.error === 'string') {
        errorMessage = errorBody.error;
      }
      if (typeof errorBody?.details === 'string' && errorBody.details) {
        errorMessage = `${errorMessage}: ${errorBody.details}`;
      }
      if (response.status === 409 && errorBody?.sentAt) {
        errorMessage = `${errorMessage} (Sent at ${new Date(errorBody.sentAt).toLocaleString()})`;
      }
    } catch {
      // ignore parse failure
    }

    if (response.status === 404) {
      throw new Error(`API endpoint not found: ${response.url}`);
    }

    throw new Error(errorMessage);
  }

  const result = await response.json();
  return {
    success: !!result?.success,
    tasksUpdated: Number(result?.updatedCount ?? result?.updatedMobileAssignments ?? 0),
    message: 'ok',
    sentAt: result?.sentAt,
  };
}
